/**
 * Gestão de Pedidos — detalhe (`/pedidos/:orderId`, §3.2 da spec + decisão 4
 * da rodada 2 + seção C da rodada 3, 2026-09-23).
 *
 * Cabeçalho com a identidade multi-canal em 4 campos (Canal / Pedido do canal
 * / Pack / Pedido Bling) + situação + tags automáticas (com selo de origem) +
 * tags manuais editáveis; linha de contexto (comprador · data · total ·
 * envio); barra de ATALHOS sempre visível. Abaixo, DUAS colunas em `lg+`:
 * as abas em blocos (Venda | Mensagens | Pós-venda | Comprador | Financeiro |
 * JSON raw só `developer`) à esquerda e a coluna fixa de histórico
 * (`HistoryRail`, `./historyRail.jsx`) à direita — abaixo de `lg` ela vira um
 * painel recolhível no fim da página. A página ocupa a largura toda do
 * conteúdo (só o gutter do Layout).
 *
 * As abas vivem em `./detailParts.jsx`, carregam sob demanda e FICAM montadas
 * (`LazyPane`) enquanto o pedido for o mesmo — trocar de aba não refaz
 * requests; `key={orderId}` as remonta ao trocar de pedido. `currentIdRef`
 * descarta respostas obsoletas das cargas da página (detalhe, histórico,
 * tags, sincronização).
 *
 * Estado de navegação NA URL (link compartilhável abre no mesmo lugar):
 * `?tab=<key>` e, na Pós-venda, `&caso=<rma_id>` do caso expandido — escritos
 * com `replace` (não poluem o histórico do navegador), demais parâmetros
 * preservados; trocar de aba remove `caso`.
 *
 * Histórico: `detail.history` é a primeira pintura; quando não vem no
 * contrato novo (`{ events, next_before, sources }`), a rota
 * `GET /pedidos/:id/history` completa (cursor + contagens). Nota/tag/sync
 * recarregam do topo; "Carregar mais" pagina por `next_before`.
 *
 * Volta para a lista: a lista passa `state.from` (`/pedidos?…` com a
 * query-string DELA); sem isso, `/pedidos`.
 *
 * Rodada 5 (2026-09-29): o histórico do comprador (`GET
 * /pedidos/:id/comprador`) carrega JUNTO com o detalhe (um request a mais) —
 * alimenta a aba Comprador e o chip de alerta ao lado do comprador no
 * cabeçalho. Casos do pack: a reclamação de ENVIO de outro pedido do pack vem
 * nas ocorrências (`scope: 'pack'`) e aparece na Pós-venda; `pack_cases`
 * (reclamações de PEDIDO dos irmãos) vira o aviso com link. A aba Financeiro
 * trata só os casos do PRÓPRIO pedido (tags e contagens ficam no pedido-âncora).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, ArrowLeft } from 'shared/icons';
import BlingOrderLinkPanel from '../../components/pedidos/BlingOrderLinkPanel';
import OrderLogisticsBadge from '../../components/pedidos/OrderLogisticsBadge';
import {
    ManualTagPicker,
    OrderStatusPill,
    PedidoIdentity,
    RowAccentClass,
    ShortcutsBar,
    TagChip,
    WITHDRAWAL_TAG_KEY,
    WithdrawalCase,
} from '../../components/pedidos/pedidosShared';
import { fmtDate } from '../../components/rma/rmaShared';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Toast';
import { ML_LOGISTIC_TYPE_LABELS, ML_SHIPPING_STATUS_LABELS, MlLabel } from '../../constants/mlClaimCatalog';
import { LIST_HEALTH_LABELS } from '../../constants/paymentHealth';
import { ConversationStateMeta, SlaMinutesByTag } from '../../constants/pedidoTags';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import { BuyerAlertChip, BuyerTabSub, CompradorTab } from './compradorTab';
import {
    ConversationState,
    DETAIL_TABS,
    FinanceiroTab,
    LazyPane,
    MensagensTab,
    PayloadsTab,
    PosVendaTab,
    ResolveDetailTab,
    ResolveFinanceiro,
    TabBlocks,
    VendaTab,
} from './detailParts';
import {
    EMPTY_HISTORY,
    HISTORY_PAGE_SIZE,
    HistoryRail,
    MergeHistoryPage,
    NormalizeHistoryResponse,
} from './historyRail';

const OPEN_OCCURRENCE_STATUSES = new Set(['ABERTO', 'EM_TRANSITO', 'RECEBIDO', 'INSPECIONADO']);
const EMPTY_BUYER = Object.freeze({ data: null, loading: false, error: '' });
const NO_PACK_CASES = [];

/** Fontes do `POST /pedidos/:id/sync` e do `detail.sync.sources`, na ordem do título. */
const SYNC_SOURCE_LABELS = {
    bling: 'Pedido Bling',
    venda: 'Venda',
    envio: 'Envio',
    // Envio de retorno das devoluções + NF-e de devolução do ML.
    devolucao: 'Devolução',
    conversa: 'Conversa',
    pagamentos: 'Pagamentos',
    ocorrencias: 'Ocorrências',
};

function sortDefinitions(list) {
    return [...list].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
}

/**
 * Uma linha por fonte para o `title` de "Última sincronização". No DTO do
 * detalhe cada fonte é a data ISO (string); na resposta do sync é
 * `{ ok, at, error }` — os dois formatos são aceitos.
 */
function syncSourcesTitle(sources) {
    return Object.entries(SYNC_SOURCE_LABELS)
        .map(([key, label]) => {
            const source = sources?.[key];
            const at = typeof source === 'string' ? source : source?.at;
            let value = '—';
            if (source?.error) {
                value = `erro — ${source.error}`;
            } else if (at) {
                value = fmtDate(at);
            } else if (source?.ok) {
                value = 'ok';
            }
            return `${label}: ${value}`;
        })
        .join('\n');
}

/** Fontes que voltaram com erro no sync, com rótulo pt-BR e motivo. */
function failedSyncSources(sources) {
    return Object.entries(sources || {})
        .filter(([, source]) => source && (source.error || source.ok === false))
        .map(([key, source]) => `${SYNC_SOURCE_LABELS[key] || key}${source.error ? ` (${source.error})` : ''}`);
}

const PedidoDetail = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('orders:write');
    const canReadAtendimento = checkPermission('atendimento:read');
    const canReply = checkPermission('atendimento:reply');
    const canReadRmas = checkPermission('rmas:read');
    const canReadPayments = checkPermission('payments:read');
    const canFinanceRmas = checkPermission('rmas:finance:update');
    const isDeveloper = checkPermission('developer');

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rechecking, setRechecking] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncVersion, setSyncVersion] = useState(0);
    const [history, setHistory] = useState(EMPTY_HISTORY);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [definitions, setDefinitions] = useState([]);
    const [tagBusy, setTagBusy] = useState(false);
    const [noteSaving, setNoteSaving] = useState(false);
    // SLA por tag (GET /pedidos/regras) — o chip do cabeçalho diz "SLA de
    // 30 min" como a lista, não só "(SLA)".
    const [slaByTag, setSlaByTag] = useState({});
    // Histórico do comprador (rodada 5): aba Comprador + chip de alerta.
    const [buyerInfo, setBuyerInfo] = useState(EMPTY_BUYER);

    // Guarda de staleness: resposta em voo de um pedido anterior é descartada.
    const currentIdRef = useRef(orderId);
    currentIdRef.current = orderId;
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const tab = ResolveDetailTab(searchParams.get('tab'), { developer: isDeveloper });
    const casoParam = searchParams.get('caso');
    const backTo =
        typeof location.state?.from === 'string' && location.state.from.startsWith('/pedidos')
            ? location.state.from
            : null;

    // ── Histórico (coluna fixa) ─────────────────────────────────────────────

    const reloadHistory = useCallback(async () => {
        const requestedId = orderId;
        setHistoryLoading(true);
        setHistoryError('');
        try {
            const data = await api.getPedidoHistory(requestedId, { limit: HISTORY_PAGE_SIZE });
            if (currentIdRef.current !== requestedId) {
                return;
            }
            setHistory(NormalizeHistoryResponse(data));
        } catch (err) {
            if (currentIdRef.current !== requestedId) {
                return;
            }
            // o histórico é derivado; a página segue com a última versão
            setHistoryError(err.message || 'Histórico indisponível agora.');
        } finally {
            if (currentIdRef.current === requestedId) {
                setHistoryLoading(false);
            }
        }
    }, [orderId]);

    const loadMoreHistory = useCallback(async () => {
        const requestedId = orderId;
        const before = history.next_before;
        if (!before || historyLoadingMore) {
            return;
        }
        setHistoryLoadingMore(true);
        try {
            const data = await api.getPedidoHistory(requestedId, { limit: HISTORY_PAGE_SIZE, before });
            if (currentIdRef.current !== requestedId) {
                return;
            }
            const page = NormalizeHistoryResponse(data);
            setHistory((current) => MergeHistoryPage(current, page));
        } catch (err) {
            if (currentIdRef.current === requestedId) {
                toastRef.current?.error(err.message || 'Não foi possível carregar mais eventos.');
            }
        } finally {
            if (currentIdRef.current === requestedId) {
                setHistoryLoadingMore(false);
            }
        }
    }, [orderId, history.next_before, historyLoadingMore]);

    // ── Detalhe ─────────────────────────────────────────────────────────────

    const load = useCallback(
        async ({ silent = false } = {}) => {
            const requestedId = orderId;
            if (!silent) {
                setLoading(true);
            }
            try {
                const data = await api.getPedido(requestedId);
                if (currentIdRef.current !== requestedId) {
                    return;
                }
                setDetail(data);
                const initial = NormalizeHistoryResponse(data?.history);
                setHistory(initial);
                if (!initial.complete) {
                    reloadHistory();
                }
            } catch (err) {
                if (currentIdRef.current !== requestedId) {
                    return;
                }
                if (!silent) {
                    setDetail(null);
                }
                toastRef.current?.error(err.message || 'Não foi possível carregar o pedido.');
            } finally {
                if (currentIdRef.current === requestedId) {
                    setLoading(false);
                }
            }
        },
        [orderId, reloadHistory],
    );

    // Depois de uma ação num caso (no caso embutido da Pós-venda ou no
    // "Conciliação dos casos" do Financeiro): refaz o pedido sem derrubar a tela
    // — cabeçalho, tags, linha do caso e histórico ficam em dia (F07).
    const reloadAfterCaseChange = useCallback(() => load({ silent: true }), [load]);

    // "Verificar de novo" da aba Mensagens: refaz o GET do detalhe (o backend
    // hidrata a conversa do ML de novo) sem derrubar a tela.
    const recheck = useCallback(async () => {
        setRechecking(true);
        try {
            await load({ silent: true });
        } finally {
            if (currentIdRef.current === orderId) {
                setRechecking(false);
            }
        }
    }, [load, orderId]);

    // "Sincronizar": `POST /pedidos/:id/sync` refaz venda/envio/conversa no ML
    // e a página recarrega detalhe + histórico. Fonte com erro vira aviso
    // nomeando a fonte — as outras seguem.
    const sync = async () => {
        const requestedId = orderId;
        setSyncing(true);
        try {
            const result = await api.syncPedido(requestedId);
            if (currentIdRef.current !== requestedId) {
                return;
            }
            const failed = failedSyncSources(result?.sources);
            if (failed.length) {
                toastRef.current?.info(`Sincronização parcial — falhou em: ${failed.join(', ')}.`);
            } else {
                toastRef.current?.success('Pedido sincronizado com o Mercado Livre.');
            }
            await load({ silent: true });
            // As abas guardam cache (LazyPane): sem remontar, a conversa e os
            // casos continuariam com o que foi lido antes do Sincronizar.
            setSyncVersion((v) => v + 1);
        } catch (err) {
            if (currentIdRef.current === requestedId) {
                toastRef.current?.error(err.message || 'Não foi possível sincronizar o pedido.');
            }
        } finally {
            if (currentIdRef.current === requestedId) {
                setSyncing(false);
            }
        }
    };

    // Histórico do mesmo comprador (buyer_id): carrega com o detalhe. Falha não
    // derruba a página — a aba mostra "indisponível" com "Tentar de novo".
    const loadBuyer = useCallback(async () => {
        const requestedId = orderId;
        setBuyerInfo((current) => ({ ...current, loading: true, error: '' }));
        try {
            const data = await api.getPedidoComprador(requestedId);
            if (currentIdRef.current === requestedId) {
                setBuyerInfo({ data: data || null, loading: false, error: '' });
            }
        } catch (err) {
            if (currentIdRef.current === requestedId) {
                setBuyerInfo({ data: null, loading: false, error: err.message || 'erro ao carregar' });
            }
        }
    }, [orderId]);

    useEffect(() => {
        setBuyerInfo(EMPTY_BUYER);
        loadBuyer();
    }, [loadBuyer]);

    // Status do financeiro salvo (aba Financeiro): a tela mostra já e o pedido
    // recarrega (tag "Pendência financeira" e histórico).
    const onFinanceReviewSaved = useCallback(
        (review) => {
            setDetail((current) =>
                current?.order ? { ...current, order: { ...current.order, finance_review: review } } : current,
            );
            return load({ silent: true });
        },
        [load],
    );

    const loadDefinitions = useCallback(async () => {
        try {
            const list = await api.listPedidoTagDefinitions();
            setDefinitions(sortDefinitions(Array.isArray(list) ? list : []));
        } catch {
            // sem definições o editor mostra "nenhuma tag manual criada"
        }
    }, []);

    useEffect(() => {
        setDetail(null);
        setHistory(EMPTY_HISTORY);
        load();
    }, [load]);

    useEffect(() => {
        loadDefinitions();
    }, [loadDefinitions]);

    useEffect(() => {
        let alive = true;
        api.getPedidoRegras()
            .then((data) => {
                if (alive) {
                    setSlaByTag(SlaMinutesByTag(data?.rules));
                }
            })
            .catch(() => {
                // Sem regras o chip diz só "(SLA)" — nada quebra.
            });
        return () => {
            alive = false;
        };
    }, []);

    // ── Tags manuais ────────────────────────────────────────────────────────

    const applyManualTags = (manualTags) => {
        setDetail((current) =>
            current ? { ...current, order: { ...current.order, manual_tags: manualTags || [] } } : current,
        );
    };

    const toggleTag = async (def, next) => {
        const requestedId = orderId;
        setTagBusy(true);
        try {
            const result = next
                ? await api.assignPedidoTag(requestedId, def.id)
                : await api.removePedidoTag(requestedId, def.id);
            if (currentIdRef.current !== requestedId) {
                return;
            }
            applyManualTags(result?.manual_tags);
            reloadHistory();
        } catch (err) {
            toastRef.current?.error(err.message || 'Não foi possível atualizar a tag.');
        } finally {
            if (currentIdRef.current === requestedId) {
                setTagBusy(false);
            }
        }
    };

    // "Nova tag" no detalhe cria a definição E aplica ao pedido em seguida. A
    // falha da criação (409 nome duplicado, 400) PROPAGA: o `ManualTagPicker`
    // mantém o formulário aberto com o texto e mostra o erro ali.
    const createTag = async ({ name, color }) => {
        const def = await api.createPedidoTagDefinition({ name, color });
        setDefinitions((current) =>
            sortDefinitions([...current.filter((item) => String(item.id) !== String(def.id)), def]),
        );
        await toggleTag(def, true);
    };

    // ── Notas ───────────────────────────────────────────────────────────────

    const addNote = async (text) => {
        const requestedId = orderId;
        setNoteSaving(true);
        try {
            await api.addPedidoNote(requestedId, text);
            if (currentIdRef.current !== requestedId) {
                return false;
            }
            toastRef.current?.success('Nota registrada.');
            reloadHistory();
            return true;
        } catch (err) {
            toastRef.current?.error(err.message || 'Não foi possível registrar a nota.');
            return false;
        } finally {
            if (currentIdRef.current === requestedId) {
                setNoteSaving(false);
            }
        }
    };

    // ── Navegação ───────────────────────────────────────────────────────────

    const goBack = () => {
        navigate(backTo || '/pedidos');
    };

    // `?tab=` com `replace` (sem poluir o histórico), demais parâmetros
    // preservados; trocar de aba solta o `caso` da Pós-venda.
    const changeTab = (key) => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                if (key === 'venda') {
                    next.delete('tab');
                } else {
                    next.set('tab', key);
                }
                next.delete('caso');
                return next;
            },
            { replace: true, state: location.state },
        );
    };

    // Caso expandido na Pós-venda: `?caso=<rma_id>` (null = recolhido).
    const changeCase = (rmaId) => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                if (rmaId === null || rmaId === undefined) {
                    next.delete('caso');
                } else {
                    next.set('caso', String(rmaId));
                }
                return next;
            },
            { replace: true, state: location.state },
        );
    };

    // "Conciliação dos casos" → abre a Pós-venda com o caso expandido.
    const openCase = (rmaId) => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.set('tab', 'posvenda');
                next.set('caso', String(rmaId));
                return next;
            },
            { replace: true, state: location.state },
        );
    };

    const onConversationChanged = (conversation) => {
        if (!conversation) {
            return;
        }
        setDetail((current) =>
            current ? { ...current, conversation: { ...(current.conversation || {}), ...conversation } } : current,
        );
    };

    // ── Render ──────────────────────────────────────────────────────────────

    // Volta à lista: não é identidade nem ação primária — sem negrito.
    const backButton = (
        <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1 text-sm text-mono-500 transition-colors hover:text-black dark:text-mono-400 dark:hover:text-white"
        >
            <ArrowLeft size={16} /> Pedidos
        </button>
    );

    if (loading && !detail) {
        return (
            <div className="flex w-full flex-col gap-4">
                <div className="flex items-center gap-3">{backButton}</div>
                <LoadingSpinner className="py-20" />
            </div>
        );
    }

    if (!detail?.order) {
        return (
            <div className="flex w-full flex-col gap-4">
                <div className="flex items-center gap-3">{backButton}</div>
                <div className="border border-mono-200 bg-white px-5 py-8 text-center dark:border-mono-800 dark:bg-mono-950">
                    <p className="font-mono text-base font-bold text-black dark:text-white">#{orderId}</p>
                    <p className="mt-1 text-sm text-mono-500 dark:text-mono-400">Pedido não encontrado neste escopo.</p>
                </div>
            </div>
        );
    }

    const {
        order,
        conversation = null,
        ocorrencias = [],
        payments_health: paymentsHealth = null,
        financeiro,
        pack_orders: packOrders = [],
        pack_cases: packCasesWire = NO_PACK_CASES,
        sync: syncInfo = null,
    } = detail;
    const packCases = Array.isArray(packCasesWire) ? packCasesWire : NO_PACK_CASES;
    // Casos do PRÓPRIO pedido (a reclamação de envio de outro pedido do pack,
    // `scope: 'pack'`, só aparece na Pós-venda — a ação financeira é no âncora).
    const ownOcorrencias = ocorrencias.filter((occ) => occ?.scope !== 'pack');
    const financeReview = order.finance_review ?? financeiro?.finance_review ?? null;
    const tags = Array.isArray(order.tags) ? order.tags : [];
    const manualTags = Array.isArray(order.manual_tags) ? order.manual_tags : [];
    const shipping = order.shipping || {};
    const buyer = order.buyer || {};
    const place = [shipping.city, shipping.uf].filter(Boolean).join('/');
    const shippingLine = [
        MlLabel(ML_SHIPPING_STATUS_LABELS, shipping.status),
        shipping.logistic_type ? MlLabel(ML_LOGISTIC_TYPE_LABELS, shipping.logistic_type) : null,
        place || null,
    ]
        .filter(Boolean)
        .join(' · ');
    const openOccurrences = ocorrencias.filter((occ) => OPEN_OCCURRENCE_STATUSES.has(occ.status)).length;
    const conversationState = ConversationState(conversation);
    const conversationMeta = conversation ? ConversationStateMeta(conversationState) : null;
    const financeiroResolved = ResolveFinanceiro(financeiro, paymentsHealth, canReadPayments);
    const healthStatus = financeiroResolved?.health?.status ?? paymentsHealth?.status ?? null;
    // `?caso=` só vale se for um caso DESTE pedido; senão é ignorado.
    const expandedCase =
        casoParam && ocorrencias.some((occ) => String(occ.id) === String(casoParam)) ? casoParam : null;

    const tabSubs = {
        venda: { text: `${FormatInt((order.items || []).length)} itens · ${FormatMoney(order.totals?.total_amount)}` },
        mensagens: conversationMeta
            ? {
                  text: conversationMeta.label.toLowerCase(),
                  tone: conversationMeta.urgent
                      ? 'danger'
                      : conversationState === 'aguardando_comprador'
                        ? 'ok'
                        : 'neutral',
              }
            : { text: conversation?.hydrate_error ? 'ML indisponível agora' : 'sem conversa no ML' },
        posvenda:
            ocorrencias.length === 0
                ? { text: packCases.length ? 'caso em outro pedido do pack' : 'nenhum caso' }
                : openOccurrences > 0
                  ? { text: `${FormatInt(openOccurrences)} em andamento`, tone: 'warn' }
                  : { text: 'todos encerrados' },
        financeiro:
            financeiroResolved === null
                ? { text: 'sem permissão' }
                : healthStatus
                  ? {
                        text: LIST_HEALTH_LABELS[healthStatus] || healthStatus,
                        tone: healthStatus === 'attention' ? 'danger' : healthStatus === 'ok' ? 'ok' : 'neutral',
                    }
                  : { text: 'sem pagamentos' },
        comprador: BuyerTabSub(buyerInfo),
        payloads: { text: 'JSON de cada fonte' },
    };
    const tabs = DETAIL_TABS.filter((item) => !item.developer || isDeveloper).map((item) => ({
        ...item,
        label: item.key === 'posvenda' ? `Pós-venda (${FormatInt(ocorrencias.length)})` : item.label,
        sub: tabSubs[item.key],
    }));

    return (
        <div className="flex w-full flex-col gap-4">
            {/* ── Topo: voltar + módulo + sincronizar ── */}
            <div className="flex flex-wrap items-start gap-3">
                {backButton}
                <span className="pt-0.5 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Gestão de Pedidos
                </span>
                {/* Sincronizar: não é a ação primária do pedido — sem negrito. */}
                <div className="ml-auto flex flex-col items-end gap-0.5">
                    <button
                        type="button"
                        onClick={sync}
                        disabled={syncing}
                        className="inline-flex items-center gap-1 text-sm text-mono-500 transition-colors hover:text-black disabled:cursor-not-allowed disabled:opacity-50 dark:text-mono-400 dark:hover:text-white"
                    >
                        <ArrowClockwise size={16} className={syncing ? 'animate-spin' : ''} />
                        {syncing ? 'Sincronizando…' : 'Sincronizar'}
                    </button>
                    <span
                        className="text-[11px] text-mono-400 dark:text-mono-500"
                        title={syncSourcesTitle(syncInfo?.sources)}
                    >
                        Última sincronização: {syncInfo?.last_synced_at ? fmtDate(syncInfo.last_synced_at) : '—'}
                    </span>
                </div>
            </div>

            {/* ── Cabeçalho: identidade em 4 campos + situação + tags + contexto ── */}
            <header
                className={`border border-l-4 border-mono-200 bg-white px-5 py-4 dark:border-mono-800 dark:bg-mono-950 ${RowAccentClass(
                    tags,
                )}`}
            >
                <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                    <PedidoIdentity order={order} size="md" />
                    <span className="flex flex-wrap items-center gap-1 pb-0.5">
                        <OrderStatusPill status={order.status} />
                        <OrderLogisticsBadge logisticType={order.shipping?.logistic_type} />
                        {tags.length ? (
                            tags.map((tag) => (
                                <TagChip
                                    key={tag.key}
                                    tag={tag}
                                    slaMinutes={slaByTag[tag.key]}
                                    info={tag.key === WITHDRAWAL_TAG_KEY ? WithdrawalCase(ocorrencias) : null}
                                />
                            ))
                        ) : (
                            <span className="text-[11px] text-mono-400 dark:text-mono-600">sem pendências</span>
                        )}
                    </span>
                </div>
                <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-mono-600 dark:text-mono-300">
                    <span>
                        {buyer.name || buyer.nickname || '—'}
                        {buyer.nickname && buyer.name ? (
                            <span className="ml-1 font-mono text-[11px] text-mono-400 dark:text-mono-500">
                                {buyer.nickname}
                            </span>
                        ) : null}
                    </span>
                    <BuyerAlertChip alert={buyerInfo.data?.alert} onClick={() => changeTab('comprador')} />
                    <span aria-hidden="true">·</span>
                    <span>{fmtDate(order.date_created)}</span>
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">{FormatMoney(order.totals?.total_amount)}</span>
                    <span aria-hidden="true">·</span>
                    <span>Envio: {shippingLine || '—'}</span>
                </p>
                <div className="mt-3 border-t border-mono-100 pt-3 dark:border-mono-900">
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                        Tags manuais
                    </p>
                    <ManualTagPicker
                        definitions={definitions}
                        selected={manualTags}
                        onToggle={toggleTag}
                        onCreate={createTag}
                        canEdit={canWrite}
                        busy={tagBusy}
                    />
                </div>
            </header>

            {/* ── Atalhos: sempre visíveis, em todas as abas (decisão 5) ── */}
            <ShortcutsBar order={order} ocorrencias={ocorrencias} />
            <BlingOrderLinkPanel key={orderId} order={order} canEdit={canWrite} onChanged={reloadAfterCaseChange} />

            {/* ── Duas colunas: abas à esquerda, histórico fixo à direita (lg+) ── */}
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),20rem]">
                <div className="flex min-w-0 flex-col gap-4">
                    <TabBlocks tabs={tabs} active={tab} onChange={changeTab} />

                    {/* Cada aba monta na primeira visita e fica viva (cache) até trocar de pedido. */}
                    <div key={`${orderId}:${syncVersion}`}>
                        <LazyPane active={tab === 'venda'}>
                            <VendaTab order={order} packOrders={packOrders} backTo={backTo} />
                        </LazyPane>
                        <LazyPane active={tab === 'mensagens'}>
                            <MensagensTab
                                orderId={order.channel_order_id || order.external_order_id}
                                packId={order.pack_id}
                                conversation={conversation}
                                ocorrencias={ocorrencias}
                                canRead={canReadAtendimento}
                                canReply={canReply}
                                toast={toast}
                                onConversationChanged={onConversationChanged}
                                onRecheck={recheck}
                                rechecking={rechecking}
                            />
                        </LazyPane>
                        <LazyPane active={tab === 'posvenda'}>
                            <PosVendaTab
                                orderId={order.external_order_id}
                                packId={order.pack_id}
                                ocorrencias={ocorrencias}
                                canReadRmas={canReadRmas}
                                expandedId={expandedCase}
                                onExpandedChange={changeCase}
                                onCaseChanged={reloadAfterCaseChange}
                                orderTagKeys={tags.map((tag) => tag.key)}
                                packCases={packCases}
                                backTo={backTo}
                            />
                        </LazyPane>
                        <LazyPane active={tab === 'comprador'}>
                            <CompradorTab state={buyerInfo} onRetry={loadBuyer} backTo={backTo} />
                        </LazyPane>
                        <LazyPane active={tab === 'financeiro'}>
                            <FinanceiroTab
                                orderId={order.external_order_id}
                                financeiro={financeiro}
                                paymentsHealth={paymentsHealth}
                                canReadPayments={canReadPayments}
                                ocorrencias={ownOcorrencias}
                                canFinance={canFinanceRmas}
                                onCaseChanged={reloadAfterCaseChange}
                                onOpenCase={openCase}
                                toast={toast}
                                orderTagKeys={tags.map((tag) => tag.key)}
                                financeReview={financeReview}
                                onFinanceReviewSaved={onFinanceReviewSaved}
                            />
                        </LazyPane>
                        {isDeveloper ? (
                            <LazyPane active={tab === 'payloads'}>
                                <PayloadsTab orderId={order.external_order_id} />
                            </LazyPane>
                        ) : null}
                    </div>
                </div>

                {/* Célula esticada (altura da coluna de conteúdo): é nela que a coluna
                    `sticky` desliza. Abaixo de lg fica no fim da página, recolhível. */}
                <div className="min-w-0">
                    <HistoryRail
                        history={history}
                        loading={historyLoading}
                        loadingMore={historyLoadingMore}
                        error={historyError}
                        canWrite={canWrite}
                        saving={noteSaving}
                        onAddNote={addNote}
                        onLoadMore={loadMoreHistory}
                        onReload={reloadHistory}
                    />
                </div>
            </div>
        </div>
    );
};

export default PedidoDetail;
